import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useAppContext } from "../../Context/AppContext";
import { BusinessMembershipFormData } from "../../types/UserData";
import { ImageUrls } from "../../types/Types";
import PersonalInfoForm from "./BusinessMembershipForms/PersonalInfoForm";
import StepsProgressBar from "./StepsProgressBar";

const BusinessContactForm = lazy(
  () => import("./BusinessMembershipForms/BusinessContactForm")
);
const PlatformSpecificForm = lazy(
  () => import("./BusinessMembershipForms/PlatformSpecificForm")
);

const STEP_LABELS: string[] = [
  "Personal Information",
  "Business Contact Details",
  "Platform Specific Information",
];

const LAZY_FALLBACK_MIN_HEIGHT = 200;
const LAZY_FALLBACK_STYLE = { minHeight: LAZY_FALLBACK_MIN_HEIGHT };
const LAZY_FALLBACK = (
  <div
    className="rounded-4 p-3 px-4"
    style={LAZY_FALLBACK_STYLE}
    aria-busy
  />
);

const INFO_ICON_SIZE = 15;

const SectionHeaderComponent = ({
  title,
  tooltip,
}: {
  title: string;
  tooltip: string;
}) => {
  const overlay = useMemo(
    () => <Tooltip id="tooltip">{tooltip}</Tooltip>,
    [tooltip]
  );
  return (
    <div className="d-flex align-items-center text-left mb-3 gap-2">
      <h5 className="mb-1">{title}</h5>
      <OverlayTrigger placement="right" overlay={overlay}>
        <img
          src="/info.png"
          alt="More information"
          width={INFO_ICON_SIZE}
          height={INFO_ICON_SIZE}
          loading="lazy"
          decoding="async"
        />
      </OverlayTrigger>
    </div>
  );
};

export const SectionHeader = memo(SectionHeaderComponent);
SectionHeader.displayName = "SectionHeader";

interface BusinessMembershipFormProps {
  defaultData?: BusinessMembershipFormData;
  showEditModal?: boolean;
  defaultImages?: ImageUrls;
}

function BusinessMembershipFormInner({
  defaultData,
  showEditModal,
  defaultImages,
}: BusinessMembershipFormProps) {
  const [step, setStep] = useState(0);
  const { user: currentUser } = useAppContext();
  const isAdmin = currentUser?.user?.isAdmin === 1;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    const cityExists = !!currentUser?.user?.city?.trim();
    const genderExists = !!currentUser?.user?.personalGender?.trim();
    const productsExists = !!currentUser?.user?.productInterested?.trim();

    if (!genderExists) {
      setStep(0);
    } else if (!cityExists) {
      setStep(1);
    } else if (!productsExists) {
      setStep(2);
    } else {
      setStep(0);
    }
  }, [
    currentUser?.user?.city,
    currentUser?.user?.personalGender,
    currentUser?.user?.productInterested,
  ]);

  useEffect(() => {
    import("./BusinessMembershipForms/BusinessContactForm");
    import("./BusinessMembershipForms/PlatformSpecificForm");
  }, []);

  const handleNext = useCallback(() => setStep((s) => s + 1), []);
  const handleBack = useCallback(() => setStep((s) => s - 1), []);

  const stepContent = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <PersonalInfoForm
            defaultData={defaultData || currentUser?.user}
            showEditModal={showEditModal}
            isAdmin={isAdmin}
            defaultImages={defaultImages || currentUser?.imageUrls}
            handleNext={handleNext}
          />
        );
      case 1:
        return (
          <Suspense fallback={LAZY_FALLBACK}>
            <BusinessContactForm
              defaultData={defaultData || currentUser?.user}
              showEditModal={showEditModal}
              isAdmin={isAdmin}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          </Suspense>
        );
      case 2:
        return (
          <Suspense fallback={LAZY_FALLBACK}>
            <PlatformSpecificForm
              defaultData={defaultData || currentUser?.user}
              showEditModal={showEditModal}
              isAdmin={isAdmin}
              handleBack={handleBack}
            />
          </Suspense>
        );
      default:
        return null;
    }
  }, [
    step,
    defaultData,
    showEditModal,
    isAdmin,
    defaultImages,
    currentUser?.user,
    currentUser?.imageUrls,
    handleNext,
    handleBack,
  ]);

  return (
    <Row className="rounded-4 p-3 px-4">
      <StepsProgressBar currentStep={step} labels={STEP_LABELS} />
      {stepContent}
    </Row>
  );
}

BusinessMembershipFormInner.displayName = "BusinessMembershipForm";

export default memo(BusinessMembershipFormInner);
